import React, { useCallback, useEffect, useRef, useState } from 'react'

export type TooltipState = {
  visible: boolean
  x: number
  y: number
  text: string
}

type UseCursorTooltipOptions = {
  cursorOffset?: number
}

export const useCursorTooltip = (options: UseCursorTooltipOptions = {}) => {
  const cursorOffset = options.cursorOffset ?? 12

  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    text: ''
  })

  const rafRef = useRef<number | null>(null)

  const showTooltip = useCallback(
    (e: React.MouseEvent, text: string) => {
      setTooltip({
        visible: true,
        x: e.clientX + cursorOffset,
        y: e.clientY + cursorOffset,
        text
      })
    },
    [cursorOffset]
  )

  const moveTooltip = useCallback(
    (e: React.MouseEvent) => {
      const x = e.clientX + cursorOffset
      const y = e.clientY + cursorOffset

      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(() => {
        setTooltip((t) => (t.visible ? { ...t, x, y } : t))
      })
    },
    [cursorOffset]
  )

  const hideTooltip = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    setTooltip((t) => ({ ...t, visible: false }))
  }, [])

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return { tooltip, showTooltip, moveTooltip, hideTooltip }
}
